import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { wsClient } from '@/lib/ws';
import {
  ShieldCheck, Lightning, LinkSimple, Copy, CheckCircle, XCircle,
  WarningCircle, Hash, Pulse, Clock, Sparkle, Heart,
} from '@phosphor-icons/react';

const CLASS_LABELS = {
  A: { label: 'Financial', color: '#ffd700' },
  B: { label: 'Lifecycle', color: '#00f0ff' },
  C: { label: 'Stories',   color: '#ff007f' },
  D: { label: 'Ephemeral', color: '#7a7a7a' },
};

const Ledger = () => {
  const [stats, setStats] = useState(null);
  const [anchors, setAnchors] = useState([]);
  const [config, setConfig] = useState({});
  const [entries, setEntries] = useState([]);
  const [classFilter, setClassFilter] = useState('all');
  const [verifying, setVerifying] = useState(null); // anchor being verified

  const load = async () => {
    const [s, a, l] = await Promise.all([
      api.get('/ledger/stats'),
      api.get('/ledger/anchors?limit=30'),
      api.get('/ledger?limit=120'),
    ]);
    setStats(s.data);
    setAnchors(a.data.anchors || []);
    setConfig(a.data.config || {});
    setEntries(l.data.entries || []);
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    const off = wsClient.onEvent((ev) => {
      if (ev.type === 'ledger_anchor') {
        load();
        toast.success(`New anchor sealed (seq → ${ev.payload?.to_seq})`);
      }
    });
    return () => { clearInterval(id); off && off(); };
  }, []);

  const filtered = useMemo(() => {
    if (classFilter === 'all') return entries;
    return entries.filter((e) => (e.class || 'C') === classFilter);
  }, [entries, classFilter]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 pb-24" data-testid="ledger-page">
      <div className="text-center mb-10">
        <ShieldCheck size={36} className="text-bx-cyan mx-auto" weight="duotone" />
        <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter mt-2">
          Public <span className="shimmer-text">Ledger</span>
        </h1>
        <p className="text-white/60 mt-2 max-w-xl mx-auto">
          Every Bubble move, milestone, donation, and meeting. Hash-chained, anchored, verifiable.
        </p>
      </div>

      {stats && <HealthStrip stats={stats} config={config} />}

      <section className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
            <LinkSimple size={20} weight="duotone" className="text-bx-volt" />
            Cryptographic Anchors
          </h2>
          {config.polygon_configured ? (
            <span className="chip text-bx-cyan border-bx-cyan/40">
              <Pulse size={11} weight="fill" /> Anchored on Polygon
            </span>
          ) : (
            <span className="chip text-bx-gold border-bx-gold/40">
              <WarningCircle size={11} weight="fill" /> Not yet externally committed
            </span>
          )}
        </div>
        <AnchorsTimeline anchors={anchors} onVerify={setVerifying} />
      </section>

      <section className="mt-12">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
            <Hash size={20} weight="duotone" className="text-bx-cyan" />
            Entries
          </h2>
          <ClassFilter value={classFilter} onChange={setClassFilter} />
        </div>
        <div className="space-y-2" data-testid="ledger-entries">
          {filtered.map((e) => <Entry key={e.id} e={e} />)}
        </div>
      </section>

      <AnimatePresence>
        {verifying && (
          <VerifyPanel anchor={verifying} onClose={() => setVerifying(null)} />
        )}
      </AnimatePresence>
    </div>
  );
};

// ----- subcomponents

const HealthStrip = ({ stats, config }) => {
  const pct = stats.anchor_batch_size
    ? Math.max(0, Math.min(100, 100 * (1 - stats.entries_until_next_anchor / stats.anchor_batch_size)))
    : 0;
  return (
    <div className="grad-border p-6 grid grid-cols-2 md:grid-cols-4 gap-5">
      <Stat label="Tip seq" value={stats.tip_seq.toLocaleString()} accent="#00f0ff" testid="stat-tip" />
      <Stat label="Total anchors" value={stats.anchors_total.toLocaleString()} accent="#ccff00" testid="stat-anchors" />
      <Stat label="Integrity check"
            value={stats.integrity_ok ? 'OK' : 'FAIL'}
            accent={stats.integrity_ok ? '#00ffc8' : '#ff007f'}
            testid="stat-integrity" />
      <div data-testid="stat-next">
        <div className="text-[10px] uppercase tracking-widest text-white/65">Next anchor in</div>
        <div className="font-display text-2xl font-black mt-1 text-bx-volt">
          {stats.entries_until_next_anchor} entries
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-bx-volt" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-1 text-[10px] text-white/60">
          or every {Math.round(stats.anchor_max_interval_s / 60)} min · whichever first
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, accent, testid }) => (
  <div data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-white/65">{label}</div>
    <div className="font-display text-2xl font-black mt-1" style={{ color: accent }}>{value}</div>
  </div>
);

const AnchorsTimeline = ({ anchors, onVerify }) => {
  if (!anchors.length) {
    return <div className="glass rounded-2xl p-10 text-center text-white/60">
      First anchor will appear after the next batch seals.
    </div>;
  }
  return (
    <div className="relative pl-6">
      <span className="absolute left-2 top-2 bottom-2 w-px bg-gradient-to-b from-bx-volt via-bx-cyan to-transparent" />
      <div className="space-y-3">
        {anchors.map((a, i) => (
          <AnchorCard key={a.id} a={a} fresh={i === 0} onVerify={() => onVerify(a)} />
        ))}
      </div>
    </div>
  );
};

const AnchorCard = ({ a, fresh, onVerify }) => {
  const polygon = a.mode === 'polygon' && a.tx_hash;
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className="relative glass rounded-2xl p-5"
                data-testid={`anchor-${a.anchored_to_seq}`}>
      <span className={`absolute -left-[1.55rem] top-6 h-3 w-3 rounded-full ${
        polygon ? 'bg-bx-cyan' : 'bg-bx-volt'
      }`} />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">
            anchor · seq {a.anchored_from_seq} → {a.anchored_to_seq}
            {a.trigger && <> · {a.trigger}</>}
          </div>
          <div className="font-mono text-sm mt-1.5 break-all">
            merkle <span className="text-bx-volt">{a.merkle_root}</span>
          </div>
          <div className="font-mono text-xs text-white/60 mt-0.5 break-all">
            tip {a.tip_hash}
          </div>
          {polygon ? (
            <a href={`https://polygonscan.com/tx/${a.tx_hash}`} target="_blank" rel="noreferrer"
               data-testid={`anchor-${a.anchored_to_seq}-poly`}
               className="text-bx-cyan text-xs font-bold mt-2 inline-flex items-center gap-1">
              view on Polygonscan <LinkSimple size={11} weight="bold" />
            </a>
          ) : (
            <div className="mt-2 chip text-bx-gold border-bx-gold/40 inline-flex">
              <WarningCircle size={10} weight="fill" /> not yet externally committed
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className="text-[10px] uppercase tracking-widest text-white/60 flex items-center gap-1">
            <Clock size={10} weight="bold" /> {relTime(a.created_at)}
          </span>
          {fresh && <span className="chip text-bx-volt border-bx-volt/40">fresh</span>}
          <button onClick={onVerify}
                  data-testid={`anchor-${a.anchored_to_seq}-verify`}
                  className="btn-primary px-3 py-1.5 rounded-full text-xs">
            Verify
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const ClassFilter = ({ value, onChange }) => {
  const options = [
    { id: 'all', label: 'All', color: 'white' },
    { id: 'A', ...CLASS_LABELS.A },
    { id: 'B', ...CLASS_LABELS.B },
    { id: 'C', ...CLASS_LABELS.C },
    { id: 'D', ...CLASS_LABELS.D },
  ];
  return (
    <div className="flex gap-1.5 flex-wrap" data-testid="class-filter">
      {options.map((o) => (
        <button key={o.id} onClick={() => onChange(o.id)}
                data-testid={`class-filter-${o.id}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest transition ${
                  value === o.id ? 'bg-white/15 ring-1' : 'hover:bg-white/5 text-white/65'
                }`}
                style={{ color: value === o.id ? (o.color || 'white') : undefined,
                         '--tw-ring-color': o.color }}>
          {o.label}
        </button>
      ))}
    </div>
  );
};

const Entry = ({ e }) => {
  const c = CLASS_LABELS[e.class || 'C'] || CLASS_LABELS.C;
  const icon = e.type?.startsWith('bubble_') ? Sparkle
            : e.type === 'human_moment_verified' ? Heart
            : e.type === 'ledger_anchor' ? LinkSimple
            : Lightning;
  const Icon = icon;
  return (
    <div data-testid={`ledger-${e.seq}`} className="glass rounded-xl p-4 font-mono text-xs">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-bx-volt shrink-0">#{e.seq}</span>
          <span className="chip text-[9px] border" style={{ color: c.color, borderColor: c.color + '66' }}>
            tier {e.class || 'C'}
          </span>
          <Icon size={12} weight="fill" style={{ color: c.color }} />
          <span className="font-bold uppercase tracking-widest truncate">{e.type}</span>
        </div>
        <span className="text-white/60 shrink-0">{relTime(e.ts)}</span>
      </div>
      <div className="mt-2 text-white/70 break-all">{JSON.stringify(e.payload)}</div>
      <div className="mt-1 text-white/55 truncate">hash {e.hash?.slice(0, 24)}…</div>
    </div>
  );
};

// ----- verify panel (real client-side SHA-256 merkle recompute)

const VerifyPanel = ({ anchor, onClose }) => {
  const [state, setState] = useState({ phase: 'fetching', server: '', local: '', steps: [] });

  useEffect(() => {
    const run = async () => {
      try {
        addStep('Fetching entries from server (hash-only)…');
        const { data } = await api.get(
          `/ledger/range?from_=${anchor.anchored_from_seq}&to=${anchor.anchored_to_seq}&limit=2000`
        );
        const layer = (data.entries || []).map((e) => e.hash);
        addStep(`Got ${layer.length} entries. Computing merkle root in your browser…`);
        const local = await computeMerkle(layer);
        addStep(`Local root: ${local}`);
        const match = local === anchor.merkle_root;
        setState((s) => ({
          ...s,
          phase: match ? 'match' : 'mismatch',
          server: anchor.merkle_root,
          local,
        }));
      } catch (err) {
        setState((s) => ({ ...s, phase: 'error', error: String(err) }));
      }
    };
    run();
    function addStep(msg) {
      setState((s) => ({ ...s, steps: [...s.steps, msg] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadProof = () => {
    const blob = new Blob([JSON.stringify({
      anchor, ...state, verified_at: new Date().toISOString(),
    }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bubblex-anchor-${anchor.anchored_to_seq}-proof.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-xl grid place-items-center px-4 py-8"
                data-testid="verify-panel">
      <motion.div initial={{ y: 20 }} animate={{ y: 0 }}
                  className="bg-[#0a0a14] rounded-3xl p-7 max-w-xl w-full ring-1 ring-white/10 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-bx-volt">verify anchor</div>
            <h3 className="font-display text-2xl font-black tracking-tight mt-1">
              seq {anchor.anchored_from_seq} → {anchor.anchored_to_seq}
            </h3>
          </div>
          <button onClick={onClose} data-testid="verify-close"
                  className="p-2 rounded-full glass-hi text-white/70 hover:text-white">
            <XCircle size={18} weight="bold" />
          </button>
        </div>

        <div className="space-y-2 mb-5">
          {state.steps.map((s, i) => (
            <div key={i} className="text-xs text-white/70 font-mono">→ {s}</div>
          ))}
        </div>

        {state.phase === 'match' && (
          <div className="rounded-xl p-4 bg-bx-cyan/10 border border-bx-cyan/30" data-testid="verify-match">
            <div className="flex items-center gap-2">
              <CheckCircle size={20} weight="fill" className="text-bx-cyan" />
              <span className="font-display font-bold text-lg text-bx-cyan">VERIFIED</span>
            </div>
            <div className="text-xs text-white/60 mt-1">
              The merkle root your browser computed exactly matches the anchor server published.
              History between seq {anchor.anchored_from_seq} and {anchor.anchored_to_seq} has not been altered.
            </div>
          </div>
        )}

        {state.phase === 'mismatch' && (
          <div className="rounded-xl p-4 bg-bx-magenta/10 border border-bx-magenta/30" data-testid="verify-mismatch">
            <div className="flex items-center gap-2">
              <XCircle size={20} weight="fill" className="text-bx-magenta" />
              <span className="font-display font-bold text-lg text-bx-magenta">MISMATCH</span>
            </div>
            <div className="text-xs text-white/60 mt-1">
              Server-claimed and locally-computed roots differ. Save the proof below and report.
            </div>
          </div>
        )}

        {state.phase === 'error' && (
          <div className="text-bx-magenta text-sm">Error: {state.error}</div>
        )}

        {(state.phase === 'match' || state.phase === 'mismatch') && (
          <>
            <div className="mt-5 space-y-2 font-mono text-xs">
              <HashRow label="server" v={state.server} />
              <HashRow label="local"  v={state.local} />
            </div>
            <div className="mt-5 flex gap-3">
              <button onClick={downloadProof} data-testid="verify-download"
                      className="btn-primary px-5 py-2.5 rounded-full text-xs flex items-center gap-1.5">
                <Copy size={12} weight="bold" /> download proof JSON
              </button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

const HashRow = ({ label, v }) => (
  <div>
    <span className="text-white/60 uppercase text-[10px] tracking-widest mr-2">{label}</span>
    <span className="text-bx-volt break-all">{v}</span>
  </div>
);

// ----- helpers

async function sha256Hex(buf) {
  const arr = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(arr)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function computeMerkle(hashes) {
  // Mirror backend: SHA-256(`${a}|${b}`) with duplicate-last on odd.
  let layer = hashes.slice();
  if (!layer.length) return '';
  while (layer.length > 1) {
    const next = [];
    for (let i = 0; i < layer.length; i += 2) {
      const a = layer[i];
      const b = i + 1 < layer.length ? layer[i + 1] : a;
      next.push(await sha256Hex(new TextEncoder().encode(`${a}|${b}`)));
    }
    layer = next;
  }
  return layer[0];
}

function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default Ledger;
